import { useCallback, useMemo, useState } from "react";

import { getProfitAndLoss } from "../../../../Api/apiClient";
import {
  calculateDateRange,
  getRangeDisplayLabel,
  getRangeSelectionLabel,
} from "../../Shared/Utils/dateRanges";
import {
  exportProfitLossToCSV,
  exportProfitLossToPDF,
} from "../utils/exportUtils";
import { transformProfitLossApiData } from "../utils/reportUtils";

export default function useProfitAndLossData(texts, toast) {
  const defaultDateRange = useMemo(() => calculateDateRange("tm"), []);
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [filters, setFilters] = useState({
    date_after: defaultDateRange.fromDate,
    date_before: defaultDateRange.toDate,
    currency: "SAR",
    period_count: 0,
    group_by: "",
    branches: [],
    costCenters: [],
    projects: [],
  });
  const [currentRangeKey, setCurrentRangeKey] = useState("tm");
  const currentRangeLabel = useMemo(
    () =>
      currentRangeKey === "custom"
        ? getRangeSelectionLabel("custom", texts, filters.date_after, filters.date_before)
        : getRangeDisplayLabel(currentRangeKey, texts),
    [currentRangeKey, filters.date_after, filters.date_before, texts]
  );

  const fetchReport = useCallback(
    async (currentFilters) => {
      setIsLoading(true);
      const finalFilters = { ...currentFilters };
      if (finalFilters.period_count === 0 || !finalFilters.group_by) {
        delete finalFilters.group_by;
      }

      try {
        const response = await getProfitAndLoss(finalFilters);
        const nextData = response.data?.rows
          ? transformProfitLossApiData(response.data)
          : transformProfitLossApiData({ columns: [], rows: [] });
        setReportData(nextData);
      } catch (error) {
        setReportData(null);
        toast({
          title: texts.reports?.error || "Error",
          description:
            error.response?.data?.errors?.[0]?.detail ||
            error.message ||
            texts.reports?.couldNotLoadReport ||
            "Could not load Profit and Loss report data.",
          status: "error",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [texts, toast],
  );

  const updateFilters = useCallback(
    async (nextFilters) => {
      setFilters(nextFilters);
      await fetchReport(nextFilters);
    },
    [fetchReport],
  );

  const handleApplyFilters = useCallback(
    (nextFilters) => {
      void updateFilters(nextFilters);
    },
    [updateFilters],
  );

  const handleCurrencyChange = useCallback(
    (currency) => {
      void updateFilters({
        ...filters,
        currency,
      });
    },
    [filters, updateFilters],
  );

  const handleSelectRange = useCallback(
    (range) => {
      setCurrentRangeKey(range);
      const dateRange = calculateDateRange(range);
      void updateFilters({
        ...filters,
        date_after: dateRange.fromDate,
        date_before: dateRange.toDate,
      });
    },
    [filters, updateFilters],
  );

  const handleCustomDateRange = useCallback(
    ({ date_after: dateAfter, date_before: dateBefore }) => {
      setCurrentRangeKey("custom");
      void updateFilters({
        ...filters,
        date_after: dateAfter,
        date_before: dateBefore,
      });
    },
    [filters, updateFilters],
  );

  const handleExport = useCallback(
    async (format) => {
      setIsExporting(true);
      try {
        if (format === "PDF") {
          exportProfitLossToPDF(reportData, filters, toast, texts);
        } else {
          exportProfitLossToCSV(reportData, filters, toast, texts);
        }
      } finally {
        setIsExporting(false);
      }
    },
    [filters, reportData, texts, toast],
  );

  return {
    currentRangeLabel,
    filters,
    isExporting,
    isLoading,
    reportData,
    fetchReport,
    handleApplyFilters,
    handleCurrencyChange,
    handleCustomDateRange,
    handleExport,
    handleSelectRange,
  };
}
